/**
 * Čistá logika detailu fotky (`MediaDetailDrawer.tsx`): cíl odkazu místa použití, stav
 * formuláře z fotky, tělo `PATCH /api/admin/upload/:id` a kdy formulář plnit.
 */
import { applyFocalPatch, type FocalLike } from "@ckgo2/shared";
import type {
  AdminMediaResponse,
  MediaUpdate,
  MediaUsage,
  MediaUsageKind,
} from "@ckgo2/shared/types";

const byId =
  (base: string) =>
  (u: MediaUsage): string =>
    `${base}/${encodeURIComponent(u.entityId)}`;

/**
 * Editor adminu, který místo použití otevře (trasy z `apps/admin/src/App.tsx`, ne veřejné
 * cesty webu). `Record` vynutí záznam pro každý druh z `MEDIA_USAGE_KINDS`; `null` = bez
 * vlastního editoru (řádek fotky odjezdu, avatar uživatele), vypíše se jen text.
 */
export const USAGE_LINKS: Record<MediaUsageKind, ((u: MediaUsage) => string) | null> = {
  destination: byId("/destinations"),
  trip: byId("/trips"),
  inspiration: byId("/inspirations"),
  blogPost: byId("/blog"),
  contentPage: byId("/content-pages"),
  guide: byId("/guides"),
  destinationTeam: byId("/destination-teams"),
  premiumUpgrade: byId("/premium-upgrades"),
  review: byId("/reviews"),
  departurePhoto: null,
  jobOffer: byId("/job-offers"),
  newsletterCampaign: byId("/newsletter/editor"),
  setting: () => "/settings",
  user: null,
};

export function usageLinkTarget(u: MediaUsage): string | null {
  return USAGE_LINKS[u.kind]?.(u) ?? null;
}

/** Lokální stav formuláře zásuvky. */
export interface DrawerForm {
  alt: string;
  title: string;
  description: string;
  tags: string[];
  focal: FocalLike;
  source: string;
  isStarred: boolean;
  countryIds: string[];
}

/** Formulář z fotky; `??` kvůli datům starého tvaru v keši (bez nových polí). */
export function drawerFormFrom(media: AdminMediaResponse): DrawerForm {
  return {
    alt: media.alt ?? "",
    title: media.title ?? "",
    description: media.description ?? "",
    tags: media.tags ?? [],
    focal: {
      focalPointX: media.focalPointX,
      focalPointY: media.focalPointY,
      focalSource: media.focalSource,
      aiFocalPointX: media.aiFocalPointX ?? null,
      aiFocalPointY: media.aiFocalPointY ?? null,
    },
    source: media.source ?? "",
    isStarred: media.isStarred ?? false,
    countryIds: media.countryIds ?? [],
  };
}

export interface DrawerPatchBody {
  alt: string;
  title: string | undefined;
  description: string | null;
  tags: string[];
  focalPointX: number;
  focalPointY: number;
  /** Jen `auto` | `manual`: stav `ai` nastavuje server, schéma PATCH ho odmítne. */
  focalSource?: NonNullable<MediaUpdate["focalSource"]>;
  source: string | null;
  isStarred: boolean;
  /** Vždy celá množina; API ji nahradí. */
  countryIds: string[];
}

/**
 * Tažení bodu v editoru: ruční režim s novým bodem, AI bod formulář drží dál (kam vede „Zpět
 * na automatický“). Přechod stavu jen přes kanonický `applyFocalPatch`.
 */
export function dragFocal(
  focal: FocalLike,
  next: { focalPointX: number; focalPointY: number },
): FocalLike {
  return applyFocalPatch(focal, { ...next, focalSource: "manual" });
}

/**
 * Změna režimu ručně / neručně proti uložené fotce, jinak `undefined`. Porovnává se jen
 * ruční ano/ne, ne celý stav: formulář `ai` (po „Zpět na automatický“) nad zastaralou ruční
 * fotkou pošle `auto` (server ho vrátí na AI bod, idempotentně), nikdy `ai`.
 */
function focalSourceChange(
  form: FocalLike,
  media: AdminMediaResponse,
): DrawerPatchBody["focalSource"] {
  const formManual = form.focalSource === "manual";
  if (formManual === (media.focalSource === "manual")) return undefined;
  return formManual ? "manual" : "auto";
}

/**
 * Tělo uložení zásuvky. `focalSource` jen při změně režimu proti uložené fotce: oprava altu
 * režim ořezu nemění.
 */
export function drawerPatchBody(form: DrawerForm, media: AdminMediaResponse): DrawerPatchBody {
  const focalSource = focalSourceChange(form.focal, media);
  return {
    alt: form.alt.trim(),
    title: form.title.trim() || undefined,
    description: form.description.trim() || null,
    tags: form.tags,
    focalPointX: form.focal.focalPointX,
    focalPointY: form.focal.focalPointY,
    ...(focalSource ? { focalSource } : {}),
    source: form.source.trim() || null,
    isStarred: form.isStarred,
    countryIds: form.countryIds,
  };
}

/**
 * Plnit formulář? Jednou pro dané `id` (`formId` = fotka, pro kterou formulář platí), a to až
 * z čerstvých dat: refetch (★ z prohlížeče, uložení) neuložené úpravy nepřepíše a stará keš
 * se do formuláře nedostane.
 */
export function shouldSyncDrawerForm(p: {
  id: string;
  formId: string | null;
  hasMedia: boolean;
  isFetching: boolean;
}): boolean {
  return p.hasMedia && !p.isFetching && p.formId !== p.id;
}
